// Void Rising
export const VOID = {
  RISE_PER_ROUND: 50,          // px risen per eligible round
  RISE_START_TURN: 0,          // void starts rising after this many turn cycles
  INITIAL_Y_OFFSET: 50,        // void starts this far above world bottom (visible from turn 0)
  NOTIFICATION_TIME: 2.0,      // seconds to show "VOID RISING" text
  RISE_TRIGGER_OFFSET: 0.05,  // threshold offset for first-frame rise effect trigger

  // Death (distinct from projectile death — smaller, void-themed)
  CRATER_RADIUS: 75,           // vs 100 for projectile
  FREEZE_MS: 500,
  ANIMATION_DURATION: 1500,
  SHAKE_INTENSITY: 60,         // vs 80 for projectile
  FLASH_ALPHA: 0.6,

  // Colors
  EDGE_COLOR: 0xff00ff,        // magenta edge glow
  DEEP_COLOR: 0x1a0033,        // dark purple body
  DARKEST_COLOR: 0x0a0010,     // very dark void body
  RISE_FLASH_COLOR: 0x8800ff,  // purple flash on rise
  RISE_FLASH_ALPHA: 0.3,

  // Edge glow
  EDGE_GLOW_DISTANCE: 20,
  EDGE_GLOW_STRENGTH: 3,

  // Glitch bars
  GLITCH_COUNT: 8,
  GLITCH_WIDTH_MIN: 10,
  GLITCH_WIDTH_MAX: 80,
  GLITCH_HEIGHT: 4,
  GLITCH_SINE_AMP: 5,          // px vertical offset
  GLITCH_CYCLE_SPEED: 3,
  GLITCH_ALPHA: 0.35,
  GLITCH_REFRESH: 0.08,        // seconds between re-randomization

  // Ambient particles drifting up from edge
  AMBIENT_MAX: 6,
  AMBIENT_SPAWN_RATE: 0.04,

  // Rise effects
  RISE_SHAKE: 15,
  RISE_SHAKE_DECAY: 0.92,
  RISE_SCREEN_SHAKE: 8,
  RISE_SCREEN_SHAKE_DECAY: 0.88,

  // Animated rise timing
  RISE_ANTICIPATION: 0.4,      // seconds before movement starts
  RISE_MOVE_DURATION: 1.1,     // seconds for smoothstep Y movement
  RISE_SETTLE: 0.5,            // visual settling after movement

  // Bubble system
  BUBBLE_MAX_CALM: 4,
  BUBBLE_MAX_RISE: 20,
  BUBBLE_SPAWN_CALM: 0.02,
  BUBBLE_SPAWN_RISE: 0.3,
  BUBBLE_SPEED_MIN: 15,
  BUBBLE_SPEED_MAX: 45,
  BUBBLE_WOBBLE_FREQ: 3.0,
  BUBBLE_WOBBLE_AMP: 4,
  BUBBLE_RADIUS_MIN: 3,
  BUBBLE_RADIUS_MAX: 10,
  BUBBLE_POP_DURATION: 0.12,
  BUBBLE_POP_SCALE: 1.8,

  // Liquid edge waves
  EDGE_SEGMENTS: 80,
  EDGE_RISE_AMP_MULT: 6.0,     // amplitude multiplier during rise
  EDGE_AMP_DECAY: 0.92,
  EDGE_HIGHLIGHT_OFFSET: 3,
  EDGE_HIGHLIGHT_ALPHA: 0.3,
  EDGE_DISTORTION_SIGMA: 35,   // gaussian width for kill bump

  // Enhanced glitch during rise
  GLITCH_COUNT_RISE: 18,       // 18 bars during rise (vs 8 calm)
  GLITCH_REFRESH_RISE: 0.03,   // faster flicker
  GLITCH_ALPHA_RISE: 0.7,      // brighter
  GLITCH_SPREAD_RISE: 40,      // wider Y-spread from edge
  GLITCH_TEAR_COUNT: 4,        // vertical tear lines
  GLITCH_TEAR_HEIGHT_MIN: 30,
  GLITCH_TEAR_HEIGHT_MAX: 80,

  // Gradient pulse
  GRADIENT_PULSE_SPEED: 2.5,
  GRADIENT_PULSE_AMP: 0.15,

  // Ambient during rise
  AMBIENT_MAX_RISE: 12,
  AMBIENT_SPAWN_RISE: 0.15,

  // Edge sharpness (razor blade layering)
  EDGE_DARK_BAND_COLOR: 0x110022,
  EDGE_DARK_BAND_WIDTH: 5,
  EDGE_DARK_BAND_ALPHA: 0.6,
  EDGE_DARK_BAND_OFFSET: 3,
  EDGE_CORE_PULSE_SPEED: 4,
  EDGE_CORE_PULSE_BASE: 0.3,
  EDGE_CORE_PULSE_AMP: 0.15,
  EDGE_CORE_RISE_BOOST: 0.3,

  // Slash marks (crescent arcs above edge)
  SLASH_SPAWN_RATE_CALM: 0.06,
  SLASH_SPAWN_RATE_RISE: 0.35,
  SLASH_MAX_CALM: 6,
  SLASH_MAX_RISE: 15,
  SLASH_LIFE_MIN: 0.1,
  SLASH_LIFE_MAX: 0.2,
  SLASH_ARC_RADIUS_MIN: 20,
  SLASH_ARC_RADIUS_MAX: 50,
  SLASH_ARC_SWEEP_MIN: Math.PI / 3,
  SLASH_ARC_SWEEP_MAX: Math.PI / 2,
  SLASH_WIDTH_MIN: 1,
  SLASH_WIDTH_MAX: 3,
  SLASH_ALPHA_MIN: 0.5,
  SLASH_ALPHA_MAX: 0.9,

  // Sweep trail (rise aftermath)
  SWEEP_TRAIL_ALPHA: 0.4,
  SWEEP_TRAIL_DECAY: 2.0,
  SWEEP_GHOST_EDGE_ALPHA: 0.25,

  // Consume burst (void kill — Sakurai juice pass)
  CONSUME_SHAKE: 75,
  CONSUME_SCREEN_SHAKE: 55,
  CONSUME_FREEZE_MS: 600,

  // Proximity aggression
  PROXIMITY_Y_RANGE: 150,
  PROXIMITY_X_RANGE: 200,
  PROXIMITY_GLOW_BOOST: 0.6,
  PROXIMITY_GLOW_WIDTH: 3,
  PROXIMITY_REACH_PX: 8,
  PROXIMITY_REACH_SIGMA: 60,
  SLASH_PROXIMITY_BIAS: 0.4,

  // Terrain erosion (Phase 6b)
  WIREFRAME_RESIST: 20,          // px below voidY before wireframe is consumed (sand consumed at voidY)
  ERODE_SCAN_DEPTH: 60,          // world px below voidY to scan for residual terrain
  ERODE_THROTTLE_FRAMES: 4,     // only run erosion every N fixed frames (void moves slowly — invisible behind gradient)
  ERODE_PARTICLE_INTERVAL: 8,    // fixed frames between erosion particle bursts during rise
  ERODE_PARTICLE_COUNT: 12,      // particles per burst along erosion line
} as const;

// Void Personality — mood system + batched retaliation tuning
export const VOID_PERSONALITY = {
  // Mood tuning
  MOOD_PER_CONSUME: 8,       // mood gain per projectile consumed by void
  MOOD_PER_KILL: 25,         // mood gain when void kills a tank
  MOOD_DECAY_PER_TURN: 15,   // mood lost per turn cycle

  // Mood thresholds
  CALM_MAX: 25,              // 0-25 = calm
  AGITATED_MAX: 55,          // 26-55 = agitated
  // 56-100 = enraged

  // Batched retaliation (replaces per-consume rolls)
  RETALIATION_CHANCE_BASE: 0.35,
  RETALIATION_CHANCE_SCALE: 0.1,   // per extra consume beyond first
  RETALIATION_CHANCE_CAP: 0.85,
  RETALIATION_ORB_BASE: 1,
  RETALIATION_ORB_SCALE: 0.5,      // per extra consume beyond first
  RETALIATION_ORB_CAP: 5,

  // Fan launch pattern
  FAN_ARC: Math.PI * 0.6,          // ~108 degrees total arc
  FAN_SPEED: -18,                   // upward velocity (matches old LAUNCH_VY)
  FAN_VX_JITTER: 1.5,              // small random horizontal jitter per orb

  // Text display chances per mood tier
  TEXT_CHANCE_CALM: 0.15,
  TEXT_CHANCE_AGITATED: 0.4,
  TEXT_CHANCE_ENRAGED: 0.7,

  // Text size per mood tier
  TEXT_SIZE_CALM: 24,
  TEXT_SIZE_AGITATED: 32,
  TEXT_SIZE_ENRAGED: 42,

  // Text color
  TEXT_COLOR: 0xff00ff,

  // Rise speed scaling by mood
  RISE_MOOD_SCALE: 0.008,   // mood 100 = 1 + 100*0.008 = 1.8x rise speed
} as const;

// Void projectile consumption & retaliation
export const VOID_CONSUME = {
  // Effect intensity (lighter than tank void death)
  FREEZE_MS: 200,
  CAMERA_SHAKE: 25,
  SCREEN_SHAKE: 15,
  FLASH_ALPHA: 0.15,
  EDGE_DISTORTION: 20,

  // Retaliation
  CHANCE: 0.5,                // 50% chance per void consume
  DELAY_MIN: 800,             // ms before first orb
  DELAY_MAX: 1200,
  ORB_MIN: 1,
  ORB_MAX: 3,
  SPREAD_X: 100,              // px spread around consume point
  LAUNCH_VY: -18,             // upward velocity
  LAUNCH_VX_MAX: 4,           // random horizontal spread
  STAGGER: 200,               // ms between each orb launch
  FORESHADOW_MS: 400,         // warning before each launch
  FORESHADOW_DISTORTION: 15,
} as const;

// Wind
export const WIND = {
  MAX_MAGNITUDE: 0.08,     // max wind force per frame (reduced from 0.15 — wind is flavor, not dominant)
  CALM_CHANCE: 0.35,       // 35% chance of no wind (increased from 0.15 — more calm periods)
  SHIFT_DISPLAY_TIME: 1.5, // seconds to show "WIND SHIFT" notification
  SHIFT_INTERVAL: 2,       // wind re-rolls every N rounds
  // Visual effects (streaks + dust)
  VISUAL: {
    MAX_STREAKS: 25,
    BASE_SPEED: 800,         // px/sec, scaled by |wind|
    LENGTH_MIN: 30,
    LENGTH_MAX: 80,
    SPAWN_RATE: 0.15,        // base chance/frame, scaled by |wind|
    ALPHA_MIN: 0.10,
    ALPHA_MAX: 0.25,
    DRIFT_RANGE: 20,         // ±20 px/sec vertical
    MAX_DUST: 10,
    DUST_SPAWN_RATE: 0.03,
    DUST_SPEED: 500,
    DUST_LIFE_MIN: 2,
    DUST_LIFE_MAX: 4,
    DUST_COLOR: 0x8899bb,    // dim blue-gray
  },
} as const;

// Bumper lines — neon vertical lines at world edges (pinball-style)
export const BUMPER = {
  Z_INDEX: 4,                    // between VOID (3) and WIND_EFFECTS (5)

  // Colors
  LINE_COLOR: 0x4488cc,          // cool blue-cyan (idle)
  LINE_BRIGHT: 0x00ffff,         // cyan (proximity/flash) — matches WALL_BOUNCE.BLADE_COLOR
  LINE_SPIKE: 0xff4444,          // red-hot spike flash — matches WALL_BOUNCE.SPIKE_COLOR
  DARK_BACKING: 0x050520,        // Sakurai dark layer
  HIGHLIGHT: 0x88ccff,           // bright highlight core

  // Idle
  IDLE_ALPHA: 0.03,              // barely visible at rest — earns visibility via proximity/flash
  DARK_WIDTH: 6,                 // dark backing stroke width
  MAIN_WIDTH: 2,                 // main neon stroke width
  HIGHLIGHT_WIDTH: 1,            // bright highlight core width

  // Proximity
  PROXIMITY_RANGE: 300,          // distance for proximity glow trigger
  MAX_ALPHA: 0.45,               // at closest distance
  TUMBLE_ONLY: true,             // walking tanks don't trigger (they just stop)

  // Flash
  FLASH_ALPHA: 1.0,              // full brightness on bounce
  FLASH_DECAY: 8.0,              // exponential decay rate per second
  WIDTH_PULSE: 2.0,              // extra px on flash
  WIDTH_DECAY: 12.0,             // width pulse decay rate per second

  // Edge darkening
  EDGE_COLOR: 0x050510,
  EDGE_ALPHA: 0.08,              // very subtle
} as const;
